import {
  DEFAULT_SITE_LANGUAGE,
  SITE_LANGUAGES,
  isSupportedSiteLanguage,
  languageFromPathname,
  localizedPath,
  type SiteLanguage,
} from "@/lib/i18n/locale";

// Path helpers used by the page-level language guards and by every
// component that builds an internal link.
//
// The list of languages, the type, and the URL-shape rules all live in
// ``src/lib/i18n/locale.ts`` — this module re-exports them so the many
// existing call sites keep working, and adds the two pathname helpers
// that only make sense on the front end.
export {
  DEFAULT_SITE_LANGUAGE,
  isSupportedSiteLanguage,
  languageFromPathname,
  localizedPath,
};
export type { SiteLanguage };

/**
 * Historical name for the language list. Kept as a mutable array
 * because callers used to treat it as one.
 */
export const SUPPORTED_SITE_LANGUAGES: SiteLanguage[] = [...SITE_LANGUAGES];

/**
 * Drop a leading language segment, if any, leaving a language-agnostic
 * path. ``/tr/news`` becomes ``/news``; ``/tr`` and ``/`` both become
 * ``/``.
 */
export function stripLanguagePrefix(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length === 0) return "/";
  if (isSupportedSiteLanguage(segments[0])) {
    const rest = segments.slice(1);
    return rest.length > 0 ? `/${rest.join("/")}` : "/";
  }
  return pathname.startsWith("/") ? pathname : `/${pathname}`;
}

/**
 * The inverse: put ``pathname`` into ``language``.
 *
 * Note the one asymmetry, which comes from ``localizedPath``: the
 * English home page is ``/``, not ``/en``. Every other English URL keeps
 * its prefix. That means the "Home" link and the language switcher both
 * point English readers at the canonical root instead of at a duplicate
 * of it, while ``/en`` stays reachable for anyone who has it linked.
 */
export function withLanguagePrefix(pathname: string, language: SiteLanguage): string {
  return localizedPath(language, stripLanguagePrefix(pathname));
}
